import { Grid2, LinearProgress } from "@mui/material";
import GraphPeriods from "../../Components/GraphPeriods/GraphPeriods";
import PlanStyle from "../PlanUsage/PlanStyle.module.scss";
import GraphContainer from "../../Components/GraphContainer/GraphContainer";
import PlanLineGraph from "../../Components/PlanLineGraph/PlanLineGraph";
import BillingSection from "../../Components/BillingSections/BillingSection";
import PlanExpire from "../../Components/PlanExpire/PlanExpire";
import ExistingAddon from "../../Components/ExistingAddon/ExistingAddon";
import { useMakeApiCalls } from "../../../../../../Hooks/useMakeApiCalls";
import { Actions } from "../../../../../../redux/constants/actionTypes";
import { getStore } from "../../../../../../Utils/storeUtils";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ReactComponent as Clonesite } from "../../../../../../GlobalAssets/svg/clonesite12.svg";
import { ReactComponent as Addon } from "../../../../AdminAssets/svg/addonImage.svg";
import { ReactComponent as PerformanceSites } from "../../../../AdminAssets/svg/performanceSites.svg";
import { ReactComponent as Staging } from "../../../../AdminAssets/svg/stagingEnv.svg";
import { ReactComponent as WAF } from "../../../../AdminAssets/svg/waf.svg";
import NewEmptyState from "../../../../../../GlobalComponents/EmptyState/NewEmptyState";
import {
  EmptyStateForPayments,
  lastSegmentPath,
} from "../../../../../../Utils/roconConstants";
import NoGraphData from "../../Components/NoGraphData/NoGraphData";
import PageLoading from "../../../../../../GlobalComponents/PageLoading/PageLoading";
import NewPopup from "../../../../../../GlobalComponents/Popup/NewPopup";
import NoData from "../../../../../../GlobalComponents/NoData/NoData";
import FormPopup from "../../../../../../GlobalComponents/FormPopup/FormPopup";
import { blockIfSiteDeleting } from "../../../../../../Utils/activatePopup";
import { millisecondsToRoundedHours } from "../../../../../../Utils/utils";

const PlanUsage = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState("Day");
  const [addonDetails, setAddonDetails] = useState([]);
  const [isStaging, setIsStaging] = useState(false);
  const [isPerformance, setIsPerformance] = useState(false);
  const [isWaf, setIsWaf] = useState(false);
  const [popup, setPopup] = useState({
    popupState: false,
    popupType: "",
    popupTitle: "",
    popupSubtitle: "",
    popupButton: false,
    popupCancel: false,
    popupButtonTitle: "",
    popupButtonFunction: () => {},
  });

  const openSiteDetails = (site) => {
    if (blockIfSiteDeleting(site, setPopup)) {
      return;
    }
    navigate(`/account/${orgId}/${site.siteId}/site-details`);
  };

  const organizationsInfo = useSelector(
    (state) => state.organizationsInfo.timelineDetails
  );

  const params = useParams();
  let orgId = params.orgId;

  const location = useLocation();
  const lastSegment = lastSegmentPath(location);

  useEffect(() => {
    getStore().dispatch({
      type: Actions.API_ALL_PLANS,
    });
    getStore().dispatch({
      type: Actions.API_SITE_OVERVIEW,
      data: {
        orgId: orgId,
      },
    });
  }, []);

  const allPlans = useSelector((state) => state.plansInfo.allPlans);

  const stgEnv = useSelector((state) =>
    state.plansInfo?.specificPlan?.specificPlans?.find((plan) => {
      return plan.licenseType === "Staging";
    })
  );

  const plan = useSelector(
    (state) => state.plansInfo?.specificPlan?.specificPlans
  );

  const basePlan = plan?.find((plan) => plan.basePlan === true);
  const basePlanSiteLimit = basePlan?.featureDetails?.find(
    (e) => e.type === "NUM_SITES"
  )?.limitOrAvailablity;

  const performanceSites = useSelector((state) =>
    state.plansInfo?.specificPlan?.specificPlans?.find((plan) => {
      return plan.licenseType === "Performance Sites";
    })
  );

  const { stagingCredits, usedCreditsTs } = useSelector((state) => ({
    stagingCredits: state.sitesInfo?.credits?.stagingCredits,
    usedCreditsTs: state.sitesInfo?.credits?.usedTs,
  }));

  let creditsUsed = millisecondsToRoundedHours(usedCreditsTs);

  const stgLimit = stgEnv?.featureDetails?.find(
    (feature) => feature?.type === "STAGING"
  )?.limitOrAvailablity;

  const performanceUsed =
    performanceSites?.featureDetails?.find((e) => e.type === "NUM_SITES")
      ?.used || 0;

  const sitesInfo = useSelector((state) => state.sitesInfo?.siteDetails?.data);

  const stgSites = sitesInfo?.filter((site) =>
    site.environment?.some((env) => env.STAGING === "STAGING")
  );

  const pfSites = sitesInfo?.filter(
    (site) => site.addOns?.performanceSite === true
  );

  const wafSites = sitesInfo?.filter((site) => site.addOns?.WAF === true);

  const usedSites = isWaf ? wafSites?.length || 0 : performanceUsed;
  const limitSites = isWaf
    ? basePlanSiteLimit || 0
    : performanceSites?.quantity;

  const progressPercentage = Math.min(
    (isStaging ? creditsUsed / stgLimit : usedSites / limitSites) * 100,
    100
  );

  useEffect(() => {
    if (allPlans?.features?.licenses) {
      const updatedAddons = allPlans.features.licenses.map((license) => {
        let icon;

        // Match the licenseType with the corresponding icon
        switch (license.licenseType) {
          case "Additional Site":
            icon = <Addon className={PlanStyle.addonIcon} />;
            break;
          case "Performance Sites":
            icon = <PerformanceSites className={PlanStyle.addonIcon} />;
            break;
          case "Staging":
            icon = <Staging className={PlanStyle.addonIcon} />;
            break;
          case "Waf":
            icon = <WAF className={PlanStyle.addonIcon} />;
            break;
          default:
            icon = <Addon className={PlanStyle.addonIcon} />;
        }

        return {
          icon,
          addonType: license.licenseType,
        };
      });

      setAddonDetails(updatedAddons);
    }
  }, [allPlans]);

  // Effect to call API when selectedPeriod changes
  useEffect(() => {
    const fetchTimelineGraphDetails = () => {
      getStore().dispatch({
        type: Actions.GET_TIMELINEGRAPH_DETAILS,
        data: {
          type: selectedPeriod,
          orgId: orgId,
          startDate: null,
          endDate: null,
        },
      });
    };

    fetchTimelineGraphDetails();
  }, [selectedPeriod]);

  useMakeApiCalls(() => {
    getStore().dispatch({
      type: Actions.API_GET_SEPCIFIC_PLAN,
      data: { orgId: orgId },
    });

    getStore().dispatch({
      type: Actions.SET_BILL_DETAILS,
      data: { orgId: orgId, paymentPage: lastSegment },
    });
  });

  const graphData = useSelector((state) => state?.paymentInfo?.graphData);

  // Use the actual base plan (its billingType is the real Monthly/Yearly cycle),
  // not specificPlans[0] — index 0 is often a Monthly add-on (WAF/Staging), which
  // made PlanExpire show "Monthly" for a Yearly plan.
  const currentPlan = useSelector((state) =>
    state.plansInfo?.specificPlan?.specificPlans?.find(
      (plan) => plan.basePlan === true
    )
  );

  const visitsData =
    graphData?.visitsGraphResponse?.map((item) => ({
      label: item.label,
      value: item.value,
    })) || [];
  const storageData =
    graphData?.storageGraphResponse?.map((item) => ({
      label: item.label,
      value: item.value,
    })) || [];
  const bandwidthData =
    graphData?.bandwidthGraphResponse?.map((item) => ({
      label: item.label,
      value: item.value,
    })) || [];

  const handelViewStg = () => {
    setIsStaging(!isStaging);
    setIsPerformance(false);
    setIsWaf(false);
  };

  const handelViewPerformance = () => {
    setIsPerformance(!isPerformance);
    setIsStaging(false);
    setIsWaf(false);
  };

  const handleViewWaf = () => {
    setIsWaf(!isWaf);
    setIsStaging(false);
    setIsPerformance(false);
  };

  return (
    <>
      {organizationsInfo ? (
        <>
          {currentPlan ? (
            <Grid2>
              <Grid2 className={PlanStyle.container}>
                <Grid2 className={PlanStyle.heading}>Plan Usage</Grid2>
                <Grid2>
                  <GraphPeriods onPeriodChange={setSelectedPeriod} />
                </Grid2>
              </Grid2>
              <Grid2 className={PlanStyle.graphContainer}>
                <GraphContainer
                  header="Storage"
                  content={
                    graphData?.storageGraphResponse ? (
                      <PlanLineGraph data={storageData} storage={true} />
                    ) : (
                      <NoGraphData />
                    )
                  }
                />

                <GraphContainer
                  header="Bandwidth"
                  content={
                    graphData?.bandwidthGraphResponse ? (
                      <PlanLineGraph data={bandwidthData} storage={true} />
                    ) : (
                      <NoGraphData />
                    )
                  }
                />

                <GraphContainer
                  header="Visits"
                  content={
                    graphData?.visitsGraphResponse ? (
                      <PlanLineGraph data={visitsData} storage={false} />
                    ) : (
                      <NoGraphData />
                    )
                  }
                />

                <GraphContainer
                  header="Plan Expire"
                  content={<PlanExpire currentPlan={currentPlan} />}
                />
              </Grid2>

              {(stgSites?.length !== 0 ||
                pfSites?.length !== 0 ||
                wafSites?.length !== 0) && (
                <Grid2 className={PlanStyle.addonContainer}>
                  <BillingSection
                    header="Add on's"
                    sideButton={"Add more"}
                    sideButtonClick={() => navigate(`/account/${orgId}/add-on`)}
                    addonUsage={true}
                    content={
                      <Grid2
                        className={
                          wafSites?.length !== 0 &&
                          stgSites?.length !== 0 &&
                          pfSites?.length !== 0
                            ? PlanStyle.stgPerformanceWaf
                            : stgSites?.length !== 0 && pfSites?.length !== 0
                            ? PlanStyle.addonContainerSmall
                            : wafSites?.length !== 0 && pfSites?.length !== 0
                            ? PlanStyle.addonContainerSmall
                            : wafSites?.length !== 0 && stgSites?.length !== 0
                            ? PlanStyle.addonContainerSmall
                            : PlanStyle.oneAddon
                        }
                      >
                        {addonDetails
                          .filter(
                            (item) =>
                              item.addonType !== "Additional Site" &&
                              (wafSites?.length === 0
                                ? item.addonType !== "Waf"
                                : true) &&
                              (stgSites?.length === 0
                                ? item.addonType !== "Staging"
                                : true) &&
                              (pfSites?.length === 0
                                ? item.addonType !== "Performance Sites"
                                : true)
                          )
                          .map((addon, index) => (
                            <>
                              <Grid2
                                key={index}
                                className={PlanStyle.addonStyle}
                              >
                                <ExistingAddon
                                  addon={addon}
                                  index={index}
                                  wafSites={wafSites}
                                  stgSites={stgSites}
                                  pfSites={pfSites}
                                  existingAddons={
                                    addon.addonType === "Staging"
                                      ? creditsUsed
                                      : addon.addonType === "Waf"
                                      ? wafSites?.length
                                      : performanceUsed
                                  }
                                  totalAddons={
                                    addon.addonType === "Staging"
                                      ? stagingCredits > 0
                                        ? Number(stagingCredits) +
                                          Number(stgLimit)
                                        : stgLimit
                                      : addon.addonType === "Waf"
                                      ? basePlanSiteLimit
                                      : performanceSites?.quantity
                                  }
                                  onView={
                                    addon.addonType === "Staging"
                                      ? handelViewStg
                                      : addon.addonType === "Performance Sites"
                                      ? handelViewPerformance
                                      : handleViewWaf
                                  }
                                />
                              </Grid2>
                            </>
                          ))}
                      </Grid2>
                    }
                  />
                </Grid2>
              )}
            </Grid2>
          ) : (
            <NewEmptyState
              header={EmptyStateForPayments.header}
              subHeader={EmptyStateForPayments.subHeader}
              icon={Clonesite}
              title={
                <span>
                  Unlock Full Access with
                  <span style={{ color: "#135FC2" }}> LaunchPad </span>
                </span>
              }
              subTitle={EmptyStateForPayments.subTitle}
            />
          )}
        </>
      ) : (
        <PageLoading />
      )}

      <NewPopup
        trigger={isStaging || isPerformance || isWaf}
        header={
          isStaging
            ? "Staging Environment"
            : isPerformance
            ? "Performance Sites"
            : "WAF Sites"
        }
        backPopup={"Close"}
        backOnClick={
          isStaging
            ? () => setIsStaging(false)
            : isPerformance
            ? () => setIsPerformance(false)
            : () => setIsWaf(false)
        }
        closePopup={() => {
          setIsStaging(false);
          setIsPerformance(false);
          setIsWaf(false);
        }}
      >
        <Grid2 className={PlanStyle.creditsContainer}>
          <Grid2 className={PlanStyle.creditsText}>
            {isStaging
              ? "Your Credits"
              : isPerformance
              ? "Performance Sites"
              : "WAF Sites"}
          </Grid2>
          <Grid2>
            {isStaging ? (
              <>
                <span className={PlanStyle.creditsUsedValue}>
                  {creditsUsed}
                </span>{" "}
                <span className={PlanStyle.creditsLimitValue}>
                  / {stgLimit} Hours used
                </span>
              </>
            ) : isPerformance ? (
              <>
                <span className={PlanStyle.creditsUsedValue}>
                  {performanceUsed}
                </span>
                <span className={PlanStyle.creditsLimitValue}>
                  / {performanceSites?.quantity} Sites are in use
                </span>
              </>
            ) : (
              <>
                <span className={PlanStyle.creditsUsedValue}>
                  {wafSites?.length}
                </span>
                <span className={PlanStyle.creditsLimitValue}>
                  / {basePlanSiteLimit} Sites are using WAF
                </span>
              </>
            )}
          </Grid2>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            style={{
              height: 10,
              borderRadius: 5,
              marginTop: "8px",
              backgroundColor: "#ffffff",
            }}
          />
        </Grid2>
        <Grid2 className={PlanStyle.label}>Usage</Grid2>
        <Grid2 className={PlanStyle.sitesOverflow}>
          {isStaging ? (
            stgSites?.length !== 0 ? (
              stgSites?.map((site, index) => (
                <Grid2
                  key={index}
                  className={PlanStyle.sitesContainer}
                  onClick={() => openSiteDetails(site)}
                >
                  <Grid2 className={PlanStyle.siteName}>{site.siteName}</Grid2>
                </Grid2>
              ))
            ) : (
              <Grid2 className={PlanStyle.noData}>
                <NoData message={"Currently there are no staging sites"} />
              </Grid2>
            )
          ) : isPerformance ? (
            pfSites?.length !== 0 ? (
              pfSites?.map((site, index) => (
                <Grid2
                  key={index}
                  className={PlanStyle.sitesContainer}
                  onClick={() => openSiteDetails(site)}
                >
                  <Grid2 className={PlanStyle.siteName}>{site.siteName}</Grid2>
                </Grid2>
              ))
            ) : (
              <Grid2 className={PlanStyle.noData}>
                <NoData message={"Currently there are no performance sites"} />
              </Grid2>
            )
          ) : wafSites?.length !== 0 ? (
            wafSites?.map((site, index) => (
              <Grid2
                key={index}
                className={PlanStyle.sitesContainer}
                onClick={() => openSiteDetails(site)}
              >
                <Grid2 className={PlanStyle.siteName}>{site.siteName}</Grid2>
              </Grid2>
            ))
          ) : (
            <Grid2 className={PlanStyle.noData}>
              <NoData message={"Currently there are no WAF sites"} />
            </Grid2>
          )}
        </Grid2>
      </NewPopup>
      {/* Sits above the sliding pane's overlay (z-index 9999) so the popup is
          visible while the usage pane is open. */}
      <div style={{ position: "relative", zIndex: 100000 }}>
        <FormPopup
          trigger={popup.popupState}
          setTrigger={setPopup}
          type={popup.popupType}
          title={popup.popupTitle}
          subtitle={popup.popupSubtitle}
          button={popup.popupButton}
          cancel={popup.popupCancel}
          buttonTitle={popup.popupButtonTitle}
          buttonFunction={popup.popupButtonFunction}
        />
      </div>
    </>
  );
};

export default PlanUsage;
